<template>
  <component
    :is="isQScrollArea ? 'q-scroll-area' : 'div'"
    :class="isQScrollArea ? undefined : 'q-py-sm column scroll'"
    :thumb-style="isQScrollArea ? thumbStyle : undefined"
    :style="isQScrollArea ? `height: ${buttonHeight}px; z-index: 1; max-width: 100%;` : `min-height: ${buttonHeight}px; max-height: 100px;`"
    style="transition: height 0.3s ease-in-out;"
  >
    <div
      class="row q-gutter-sm q-pl-sm"
      :class="{ 'no-wrap': noWrap }"
    >
      <div
        v-for="showingForCurrentShowingDate in showingsForCurrentShowingDate"
        :key="showingForCurrentShowingDate.id"
        ref="showtimesButton"
        class="col-auto"
      >
        <showtimes-button-compact
          compact
          showtimes-grid-button-use-button-drawer
          :showing="showingForCurrentShowingDate"
          is-showing-button
          :class="isCurrentShowing(showingForCurrentShowingDate) ? 'selected-showing' : undefined"
          :button-type="isCurrentShowing(showingForCurrentShowingDate) ? BUTTON_TYPES.UNELEVATED : BUTTON_TYPES.OUTLINE"
          :button-color="isCurrentShowing(showingForCurrentShowingDate) ? 'primary' : 'responsive-grey-7'"
          :distinct-showing-badges="showingBadges(showingForCurrentShowingDate)"
          @select-showing="handleShowingForCurrentShowingDateClick(showingForCurrentShowingDate)"
        />
        <showtime-attendance-bar
          :showing="showingForCurrentShowingDate"
          class="absolute attendance-bar"
        />
        <q-tooltip
          :offset="[10, 10]"
        >
          {{ ticketLimitMessage(showingForCurrentShowingDate) }}
        </q-tooltip>
      </div>
    </div>
  </component>
</template>

<script>
import { scroll } from 'quasar'
import { BUTTON_TYPES } from 'src/models/page/Section'
import Showing from 'src/models/showing/Showing'
import ActionDialog from 'src/modules/consumer/components/ActionDialog'
import ShowtimeAttendanceBar from 'src/modules/consumer/components/ShowtimeAttendanceBar'
import ShowtimesButtonCompact from 'src/modules/consumer/components/ShowtimesButtonCompact'
export default {
  name: 'CurrentShowingDateShowings',
  components: {
    ShowtimeAttendanceBar,
    ShowtimesButtonCompact
  },
  inject: ['checkoutData', 'order'],
  props: {
    showing: {
      type: Showing,
      required: true
    },
    noWrap: {
      type: Boolean,
      required: false,
      default: false
    },
    isQScrollArea: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  configuration: ['currentSite'],
  data () {
    return {
      buttonHeight: 0
    }
  },
  models: {
    showingsForDate: {
      movie: 'default',
      screen: 'default',
      showingBadges: 'default',
      variables () {
        return {
          date: this.currentSite.siteEffectiveDateForTime(this.showing?.time).format('YYYY-MM-DD'),
          movieId: this.showing?.movie?.id,
          siteIds: [this.currentSite.id],
          skip: !this.showing?.loaded
        }
      }
    }
  },
  computed: {
    showingId () {
      const showingId = this.$kiosk ? this.$route.params?.showing : this.$route.params?.showingId
      return (_.isNil(showingId) || showingId === '') ? null : Number(showingId)
    },
    showingsForCurrentShowingDate () {
      // Filter out showings that don't have validOnlinePurchaseTime or reservedSeating
      return _.filter(this.showingsForDate?.data, showing => showing.validOnlinePurchaseTime && showing.reservedSeating)
    },
    hasTicketSelectionError () {
      return _.some(this.checkoutData.sessionData, showingData => showingData.hasTicketSelectionError)
    },
    hasSeatChartError () {
      return _.some(this.checkoutData.sessionData, showingData => showingData.hasSeatChartError)
    },
    canProceed () {
      return this.checkoutData.canProceed
    },
    draftShowingTicketOrderItems () {
      return this.order.draftShowingTicketOrderItems(this.showingId)
    },
    thumbStyle () {
      return {
        backgroundColor: this.currentSite.primaryColor,
        borderRadius: '2px',
        height: '4px',
        opacity: '0.5'
      }
    }
  },
  mounted () {
    setTimeout(() => {
      this.$nextTick(this.scrollShowingsToSelectedShowing())
    }, 500)
  },
  created () {
    _.extend(this, { BUTTON_TYPES })
  },
  methods: {
    isCurrentShowing (showing) {
      return showing.id === this.showingId
    },
    requestLeavePage (leavePageCallback) {
      if (this.canProceed) {
        leavePageCallback?.()
        return
      }

      if (!this.hasSeatChartError && !this.hasTicketSelectionError) {
        leavePageCallback?.()
        return
      }

      this.$q.dialog({
        component: ActionDialog,
        componentProps: {
          title: this.$t('checkout.errors.seatSelection.title'),
          message: this.$t('checkout.errors.seatSelection.message'),
          okLabel: this.$t('checkout.errors.seatSelection.okLabel'),
          cancelLabel: this.$t('checkout.errors.seatSelection.cancelLabel')
        }
      }).onOk(() => {
        // Fix checkout errors
        this.$router.push(`/checkout/seats/${this.showingId || ''}`)
      }).onCancel(() => {
        // Clear error order items and checkout state
        _.each(this.draftShowingTicketOrderItems, orderItem => {
          this.order.removeOrderItem(orderItem)
          this.checkoutData.removeSeats.push(orderItem)
        })

        delete this.checkoutData.sessionData[this.showingId.toString()]

        leavePageCallback?.()
      })
    },
    handleShowingForCurrentShowingDateClick (showingForCurrentShowingDate) {
      if (this.isCurrentShowing(showingForCurrentShowingDate)) return

      this.requestLeavePage(() => {
        this.$router.push(`/checkout/seats/${showingForCurrentShowingDate.id}`)
        this.checkoutData.showtimeChangeCount++
      })
    },
    ticketLimitMessage (showing) {
      const seatCount = showing.screen.seatCount
      const unavailableCount = showing.heldSeatCount + showing.ticketsSold
      const capacityPercent = ((unavailableCount / seatCount) * 100).toFixed(2)

      return this.$t('seatChart.capacityPercentFull', { count: capacityPercent })
    },
    scrollShowingsToSelectedShowing () {
      const selectedShowing = document.querySelector('.selected-showing')
      const scrollableContainer = scroll.getScrollTarget(selectedShowing)
      this.buttonHeight = selectedShowing?.clientHeight + 5 || 60

      if (!selectedShowing || !scrollableContainer) return

      if (this.$q.screen.lt.sm || !this.$isLandscapeView()) {
        const offsetLeft = selectedShowing.offsetLeft
        const scrollableContainerWidth = window.innerWidth - 100

        if (offsetLeft > scrollableContainerWidth) {
          const scrollAnimationMs = 500
          scroll.setHorizontalScrollPosition(scrollableContainer, offsetLeft, scrollAnimationMs)
        }
      } else {
        const offsetTop = selectedShowing.offsetTop
        const scrollOffsetTop = offsetTop - 200

        if (offsetTop > 210) {
          const scrollAnimationMs = 200
          scroll.setVerticalScrollPosition(scrollableContainer, scrollOffsetTop, scrollAnimationMs)
        }
      }
    },
    showingBadges (showingForCurrentShowingDate) {
      return _.filter(showingForCurrentShowingDate.showingBadges.data, showingBadge => showingBadge.isButtonOnly)
    }

  }
}
</script>
<style lang="scss" scoped>
.attendance-bar{
  height: 6px;
  transition: all 0.3s ease-in-out;
  margin: -7px 1px 0;
  border-radius: 0 0 3px 3px;
  &:hover{
    height: 10px;
    margin: -11px 1px 0;
  }

  &.compact-button{
    border-radius: 0 0 5px 5px;
    margin: -6px 0px 0;
    &:hover{
      height: 10px;
      margin: -10px 0px 0;
    }
  }
  &.slider-button{
    margin: -9px 1px 0;
    &:hover{
      height: 9px;
      margin: -12px 0px 0;
    }
  }
}
</style>
